import { type ChangeEvent, useEffect, useState } from 'react';
import { ActionIcon, Button, Group, Image, LoadingOverlay, Modal, Stack } from '@mantine/core';
import { DateTimePicker } from '@/components/DateTimePicker';
import dayjs from 'dayjs';
import { Icon } from '@/components';
import { SaveErrorBox } from '@/components/SaveErrorBox/SaveErrorBox';
import type { BackgroundUpsert } from '@/types/backgrounds';
import { useBackgroundById } from '@/hooks/backgrounds';
import type { AxiosError } from 'axios';
import type { ErrorResponse } from '@/types/error';
import modalStyles from '@/components/EditFormModal/EditFormModal.module.sass';

export type BackgroundModalProps = {
  opened: boolean;
  onClose: () => void;
  onSave: (data: BackgroundUpsert) => Promise<void> | void;
  id?: string;
};

export const BackgroundModal = ({ opened, onClose, onSave, id }: BackgroundModalProps) => {
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [currentImagePath, setCurrentImagePath] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const isDateRangeInvalid = startDate !== null && endDate !== null && startDate > endDate;
  const [saveError, setSaveError] = useState<AxiosError<ErrorResponse> | null>(null);

  const { data: bg, isLoading } = useBackgroundById(opened ? id : undefined);

  const resetForm = () => {
    setStartDate(null);
    setEndDate(null);
    setFile(null);
    setPreviewUrl(null);
    setCurrentImagePath(null);
  };

  useEffect(() => {
    if (!opened) return;
    if (!id) {
      resetForm();
      return;
    }
    if (bg) {
      setStartDate(bg.startDate);
      setEndDate(bg.endDate);
      setPreviewUrl(bg.imageUrl ?? null);
      setCurrentImagePath(bg.imagePath ?? null);
      setFile(null);
    }
  }, [opened, id, bg]);

  const handleBrowse = (e: ChangeEvent<HTMLInputElement>) => {
    const nextFile = e.target.files?.[0] ?? null;
    if (!nextFile) return;
    setFile(nextFile);
    const url = URL.createObjectURL(nextFile);
    setPreviewUrl(url);
  };

  const handleRemoveImage = () => {
    setFile(null);
    setPreviewUrl(null);
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!startDate || !endDate) return;
    if (isDateRangeInvalid) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await onSave({
        startDate: dayjs(startDate).toISOString(),
        endDate: dayjs(endDate).toISOString(),
        file: file as File,
        image: { path: currentImagePath ?? '', removeCurrentImage: false },
      });
      onClose();
    } catch (error) {
      console.error('Failed to save background', error);
      setSaveError(error as AxiosError<ErrorResponse> | null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartChange = (value: string | null) => {
    setStartDate(value);
  };

  const handleEndChange = (value: string | null) => {
    setEndDate(value);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={id ? 'Edit Background' : 'New Background'}
      size="lg"
      classNames={{
        header: modalStyles.modalHeader,
        title: modalStyles.modalTitle,
        close: modalStyles.closeButton,
      }}
    >
      <Stack pos="relative" mt="md">
        <LoadingOverlay visible={isLoading} />
        <DateTimePicker
          label="Start with"
          value={startDate}
          onChange={handleStartChange}
          required
          error={isDateRangeInvalid ? 'Start date must be before end date' : undefined}
        />
        <DateTimePicker
          label="End with"
          value={endDate}
          onChange={handleEndChange}
          required
          error={isDateRangeInvalid ? 'Start date must be before end date' : undefined}
        />
        <Stack>
          {previewUrl ? (
            <Group>
              <Image src={previewUrl} alt="Preview" h={120} w={216} fit="cover" radius="sm" />
              <ActionIcon color="red" aria-label="remove image" variant="subtle" onClick={handleRemoveImage}>
                <Icon name="IconTrash" />
              </ActionIcon>
            </Group>
          ) : (
            <Button component="label">
              Browse
              <input type="file" accept="image/*" onChange={handleBrowse} style={{ display: 'none' }} />
            </Button>
          )}
        </Stack>
        <SaveErrorBox error={saveError} />
        <Group justify="flex-end">
          <Button variant="default" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isSaving}
            disabled={!startDate || !endDate || isDateRangeInvalid || (!file && !previewUrl)}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
